import { useState, useRef } from 'react';
import { supabase } from '@/core/database/supabase';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

interface EstimateImportModalProps {
  companyId: string;
  projectId?: string | null;
  estimateId: string;
  onClose: () => void;
}

export function EstimateImportModal({ companyId, projectId, estimateId, onClose }: EstimateImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      parseCSV(selected);
    }
  };

  const parseCSV = (file: File) => {
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((l) => l.trim().length > 0);
        // Assuming CSV format: item_code, item_name, category_code, unit, quantity, unit_price
        const data = lines.slice(1).map((line) => {
          // A simple split by comma handling quotes would be better, but split(',') for now
          const row = line.split(',').map((cell) => cell.replace(/^"/, '').replace(/"$/, '').trim());
          return {
            item_code: row[0] || '',
            item_name: row[1] || '',
            category_code: row[2] || 'trees',
            unit: row[3] || 'cây',
            quantity: Number(row[4]) || 1,
            unit_price: Number(row[5]) || 0,
            material_cost: (Number(row[4]) || 1) * (Number(row[5]) || 0),
          };
        });
        setPreviewData(data);
      } catch (err) {
        toast.error('File CSV không đúng định dạng');
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsText(file);
  };

  const importMutation = useMutation({
    mutationFn: async (items: any[]) => {
      const payload = items.map((item, index) => ({
        company_id: companyId,
        project_id: projectId,
        estimate_id: estimateId,
        ...item,
        sort_order: index + 1,
        status_code: 'draft',
      }));

      const { error } = await supabase.from('estimate_items').insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates', 'items', estimateId] });
      toast.success(`Import thành công ${previewData.length} hạng mục`);
      onClose();
    },
    onError: (error: any) => {
      toast.error('Lỗi khi import: ' + error.message);
    },
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <h3 className="font-medium text-slate-900">Import dự toán từ CSV</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:bg-slate-100 h-8 w-8 rounded-md flex items-center justify-center">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="px-4 py-2 bg-emerald-50 text-emerald-700 font-medium text-sm rounded-md border border-emerald-200 hover:bg-emerald-100"
            >
              Chọn file CSV
            </button>
            <input 
              type="file" 
              accept=".csv" 
              className="hidden" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
            />
            {file && <span className="text-sm text-slate-600">{file.name}</span>}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Định dạng cột bắt buộc (không có tiêu đề dòng đầu tiên hoặc có thì sẽ bị bỏ qua): Mã hạng mục, Tên hạng mục, Nhóm, Đơn vị, Khối lượng, Đơn giá.
          </p>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-slate-50">
          {isParsing ? (
            <p className="text-sm text-slate-500">Đang đọc file...</p>
          ) : previewData.length > 0 ? (
            <table className="w-full text-left text-sm border-collapse border border-slate-200 bg-white">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 border border-slate-200">Mã HM</th>
                  <th className="p-2 border border-slate-200">Tên HM</th>
                  <th className="p-2 border border-slate-200">Nhóm</th>
                  <th className="p-2 border border-slate-200">ĐVT</th>
                  <th className="p-2 border border-slate-200">Khối lượng</th>
                  <th className="p-2 border border-slate-200">Đơn giá</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 50).map((row, i) => (
                  <tr key={i}>
                    <td className="p-2 border border-slate-200">{row.item_code}</td>
                    <td className="p-2 border border-slate-200">{row.item_name}</td>
                    <td className="p-2 border border-slate-200">{row.category_code}</td>
                    <td className="p-2 border border-slate-200">{row.unit}</td>
                    <td className="p-2 border border-slate-200 text-right">{row.quantity}</td>
                    <td className="p-2 border border-slate-200 text-right">{row.unit_price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-500">Chưa có dữ liệu preview</p>
          )}
          {previewData.length > 50 && (
             <p className="text-xs text-slate-500 mt-2 italic">Hiển thị tối đa 50 dòng đầu tiên.</p>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-slate-100">
           <div className="text-sm text-slate-600">
             {previewData.length > 0 && <span>Tổng cộng: {previewData.length} dòng hợp lệ</span>}
           </div>
           <div className="flex gap-2">
             <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-md hover:bg-slate-50">Hủy</button>
             <button 
               onClick={() => importMutation.mutate(previewData)} 
               disabled={previewData.length === 0 || importMutation.isPending}
               className="px-4 py-2 text-sm bg-emerald-700 text-white rounded-md hover:bg-emerald-800 disabled:opacity-50"
             >
               {importMutation.isPending ? 'Đang Import...' : 'Xác nhận Import'}
             </button>
           </div>
        </div>
      </div>
    </div>
  );
}
